/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import PageContainer from "../../components/PageContainer";
import ResponsiveTable from "../../components/ResponsiveTable";
import { useUI } from "../../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../../hooks/useTenantStorePermissions";
import NoAccess from "../../components/NoAccess";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiSearch,
  FiFilter,
  FiLayout,
  FiArrowUp,
  FiArrowDown,
  FiMoreHorizontal,
  FiInfo,
  FiCheck,
  FiChevronDown,
  FiRotateCcw,
  FiCreditCard,
  FiX
} from "react-icons/fi";
import { AnimatePresence, motion } from "framer-motion";
import clsx from "clsx";
import Swal from "sweetalert2";
import { pickActiveDeliveryCompany } from "../../../Stores/utils/storeApi";

export default function AdminOrdersPage() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const perms = useTenantStorePermissions();
  const navigate = useNavigate();

  // --- State Hooks (MUST BE AT TOP LEVEL) ---
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);

  // UI State
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  // New Sort Config: key + direction
  const [sortConfig, setSortConfig] = useState({ key: "date", direction: "desc" });

  // Logic for Status Change Dropdown
  const [openStatusId, setOpenStatusId] = useState(null);
  const [openPaymentStatusId, setOpenPaymentStatusId] = useState(null);

  // Logic for Sort Dropdown
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortMenuRef = useRef(null);

  // Delivery info map: cityId -> { name, fee }
  const [deliveryMap, setDeliveryMap] = useState({});

  // Bulk Actions State
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkPayment, setBulkPayment] = useState("");
  const actionsMenuRef = useRef(null);

  const TRAILING_STATUSES = ["all", "new", "preparing", "ready", "delivering", "delivered", "returned", "cancelled"];

  // --- Constants ---
  const SORT_OPTIONS = [
    { key: "orderNo", direction: "asc", label_ar: "رقم الطلب تصاعديًا", label_en: "Order ID Asc" },
    { key: "orderNo", direction: "desc", label_ar: "رقم الطلب تنازليًا", label_en: "Order ID Desc" },
    { key: "customer", direction: "asc", label_ar: "العميل تصاعديًا", label_en: "Customer Asc" },
    { key: "customer", direction: "desc", label_ar: "العميل تنازليًا", label_en: "Customer Desc" },
    { key: "totalAmount", direction: "asc", label_ar: "المجموع تصاعديًا", label_en: "Total Asc" },
    { key: "totalAmount", direction: "desc", label_ar: "المجموع تنازليًا", label_en: "Total Desc" },
    { key: "date", direction: "asc", label_ar: "تاريخ الإنشاء تصاعديًا", label_en: "Date Created Asc" },
    { key: "date", direction: "desc", label_ar: "تاريخ الإنشاء تنازليًا", label_en: "Date Created Desc" },
    // "Date Modified" logic would need a 'modifiedAt' field, falling back to createdAt/date if missing
    { key: "date", direction: "asc", label_ar: "تاريخ التحديث تصاعديًا", label_en: "Date Update Asc" },
    { key: "date", direction: "desc", label_ar: "تاريخ التحديث تنازليًا", label_en: "Date Updated Desc" },
  ];

  const STATUS_TABS = [
    { key: "all", label_ar: "الكل", label_en: "All" },
    { key: "new", label_ar: "جديد", label_en: "New" },
    { key: "preparing", label_ar: "جاري التجهيز", label_en: "Preparing" },
    { key: "ready", label_ar: "جاهز", label_en: "Ready" },
    { key: "delivering", label_ar: "جاري التوصيل", label_en: "Delivering" },
    { key: "delivered", label_ar: "مكتمل", label_en: "Completed" },
    { key: "returned", label_ar: "مسترجع", label_en: "Returned" },
    { key: "partially_returned", label_ar: "مسترجع جزئيًا", label_en: "Partially Returned" },
    { key: "cancelled", label_ar: "ملغي", label_en: "Cancelled" },
    { key: "failed", label_ar: "فشل التوصيل", label_en: "Delivery Failed" },
    { key: "manual", label_ar: "طلب يدوي", label_en: "Manual" },
  ];
  const ORDER_STATUSES = [
    { id: 1, ar: "جديد", en: "New", key: "new", color: "bg-blue-100 text-blue-700 border-blue-200" },
    { id: 2, ar: "جاري التجهيز", en: "Preparing", key: "preparing", color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
    { id: 3, ar: "جاهز", en: "Ready", key: "ready", color: "bg-purple-100 text-purple-700 border-purple-200" },
    { id: 4, ar: "جاري التوصيل", en: "In Delivery", key: "delivering", color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
    { id: 5, ar: "مكتمل", en: "Completed", key: "delivered", color: "bg-green-100 text-green-700 border-green-200" },
    { id: 6, ar: "مسترجع", en: "Returned", key: "returned", color: "bg-orange-100 text-orange-700 border-orange-200" },
    { id: 8, ar: "مسترجع جزئيًا", en: "Partially Returned", key: "partially_returned", color: "bg-teal-100 text-teal-700 border-teal-200" },
    { id: 7, ar: "ملغي", en: "Cancelled", key: "cancelled", color: "bg-red-100 text-red-700 border-red-200" },
    { id: 9, ar: "فشل التوصيل", en: "Delivery Failed", key: "failed", color: "bg-black text-white border-gray-800" },
  ];
  // Mapping API status keys to our Tabs
  // تحويل أي قيمة قادمة من الباك إند إلى مفتاح (key) داخلي نستخدمه في الـ Tabs
  const normalizeStatusKey = (raw) => {
    if (!raw) return "new";
    const v = raw.toString().toLowerCase().trim();

    // البحث في المصفوفة الموحدة عن الاسم العربي أو الإنجليزي أو المفتاح
    const found = ORDER_STATUSES.find(s =>
      s.ar === raw ||
      s.en.toLowerCase() === v ||
      s.key === v
    );

    return found ? found.key : "new";
  };

  // جلب الـ ID الصحيح لإرساله في الـ API عند التحديث
  const getStatusId = (key) => {
    const found = ORDER_STATUSES.find(s => s.key === key);
    return found ? found.id : 1;
  };

  const baseUrl = process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api";
  const tenantId = parseInt(localStorage.getItem("tenantId") || "-1");

  /* ================= Fetch Orders ================= */
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        const params = new URLSearchParams();
        params.append("tenantId", tenantId.toString());
        params.append("statusId", "-1");

        const res = await fetch(
          `${baseUrl}/GetTenantOrders?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Accept: "*/*",
              Authorization: token ? `Bearer ${token}` : "",
            },
          }
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();

        const calcOrderAmount = (order) => {
          if (Array.isArray(order.details) && order.details.length > 0) {
            return order.details.reduce((sum, i) => sum + (Number(i.lineTotal) || 0), 0);
          }
          return Number(order.netAmount || order.totalAmount || 0);
        };

        const parseOrderDate = (value) => {
          if (!value) return "-";
          const fixed = value.replace(/\s+/g, " ").replace(/(\d)(AM|PM)/, "$1 $2");
          const d = Date.parse(fixed);
          if (!isNaN(d)) {
            return new Date(d).toISOString().split('T')[0];
          }
          return value;
        };

        const mapped = Array.isArray(data)
          ? data.map((order) => {
            const rawStatus = order.status || "";
            const statusKey = normalizeStatusKey(rawStatus);
            // Use orderId (UUID) as the primary internal identifier
            const internalId = order.orderId;
            const displayNo = order.orderNo || order.orderNumber || 0;

            return {
              id: internalId, // Internal tracking (UUID)
              orderId: internalId, // UUID for API
              orderNo: Number(displayNo), // Ensure it's a number
              customer: order.customerName || "",
              phone: order.customerPhone1 || "",
              governorate: order.city || "",
              date: parseOrderDate(order.orderDate || order.createdAt),
              rawDate: order.orderDate || order.createdAt,
              totalAmount: calcOrderAmount(order),
              currency: "د.ع",
              statusKey,
              originalStatus: rawStatus,
              isManual: order.isManual === 1 || order.isManual === true || order.transactionId === "manual",
              rawOrder: order,
              isPaid:
                order.isPaid === true ||
                order.isPaid === 1 ||
                (order.paymentStatus || "").toLowerCase() === "paid",
            };
          })
          : [];

        // De-duplicate ONLY by unique orderId (UUID)
        const uniqueOrders = [];
        const seenIds = new Set();
        mapped.forEach(o => {
          if (o.id && !seenIds.has(o.id)) {
            seenIds.add(o.id);
            uniqueOrders.push(o);
          }
        });

        setOrders(uniqueOrders);

        // Fetch delivery info for all unique cityIds
        const uniqueCityIds = [...new Set(
          Array.isArray(data) ? data.map(o => o.cityId).filter(Boolean) : []
        )];
        if (uniqueCityIds.length > 0) {
          const token2 = localStorage.getItem("authToken");
          const results = await Promise.allSettled(
            uniqueCityIds.map(cId =>
              fetch(`${baseUrl}/TenantDelivery/GetByCity?tenantId=${tenantId}&cityId=${cId}`, {
                headers: { Authorization: token2 ? `Bearer ${token2}` : "" }
              }).then(r => r.ok ? r.json() : null).then(arr => ({ cId, arr }))
            )
          );
          const map = {};
          results.forEach(r => {
            if (r.status === "fulfilled" && r.value?.arr) {
              const activeDelivery = pickActiveDeliveryCompany(r.value.arr);
              if (activeDelivery) {
                map[r.value.cId] = {
                  name: activeDelivery.deliveryCompanyName,
                  fee: Number(activeDelivery.deliveryCompanyFees ?? 0)
                };
              }
            }
          });
          setDeliveryMap(map);
        }

      } catch (err) {
        console.error("GetTenantOrders error:", err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    if (tenantId !== -1) fetchOrders();
    else setLoading(false);
  }, [baseUrl, tenantId, isArabic]);

  // --- Derived State: Counts ---
  const statusCounters = useMemo(() => {
    const acc = {};
    // Initialize all to 0
    STATUS_TABS.forEach(t => acc[t.key] = 0);

    orders.forEach(o => {
      // Increment specific status
      if (acc[o.statusKey] !== undefined) {
        acc[o.statusKey]++;
      }
      // Increment manual count
      if (o.isManual) {
        acc["manual"]++;
      }
      // Increment 'all' for every order
      acc['all']++;
    });
    return acc;
  }, [orders]);


  // --- Helper Functions for API ---
  const getstatusId = (key) => {
    const map = {
      new: 1,
      preparing: 2,
      ready: 3,
      delivering: 4,
      delivered: 5,
      returned: 6,
      cancelled: 7,
      partially_returned: 8,
      failed: 9
    };
    return map[key] || 1;
  };

  const toPascalDetails = (details) => {
    if (!Array.isArray(details)) return [];
    return details.map(item => {
      const rawQty = Number(item.quantity || 0);
      const rawStock = Number(item.stockQty || 0);
      const safeStock = Number.isFinite(rawStock) ? Math.max(0, rawStock) : 0;
      const safeQty = safeStock <= 0
        ? 0
        : (Number.isFinite(rawQty) ? Math.max(0, Math.min(rawQty, safeStock)) : 0);
      const safePrice = Number(item.price || 0);
      const safeDiscount = Number(item.discount || 0);
      const resolvedImageUrl =
        item.imageUrl ||
        item.ImageUrl ||
        item.mainImageUrl ||
        item.MainImageUrl ||
        item.itemMainImageUrl ||
        item.parentImageUrl ||
        item.productImageUrl ||
        item?.item?.mainImageUrl ||
        item?.product?.mainImageUrl ||
        "";
      return {
        Id: item.id || 0,
        InvoiceNo: item.invoiceNo || "",
        ItemId: Number(item.itemId || 0),
        VariantId: Number(item.variantId || 0),
        ItemName: item.itemName || "",
        VariantName: item.variantName || "",
        ColorName: item.colorName || "",
        Size: item.size || "",
        Quantity: safeQty,
        Price: safePrice,
        ImageUrl: resolvedImageUrl,
        StockQty: safeStock,
        Discount: safeDiscount,
        LineTotal: safeQty * safePrice - safeDiscount
      };
    });
  };

  // --- Filtering & Sorting ---
  const filteredOrders = useMemo(() => {
    let result = [...orders];

    // 1. Filter by Tab
    if (statusFilter !== "all") {
      if (statusFilter === "manual") {
        result = result.filter(o => o.isManual);
      } else {
        result = result.filter(o => o.statusKey === statusFilter);
      }
    }

    // 2. Filter by Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(o =>
        (o.orderNo && o.orderNo.toString().toLowerCase().includes(q)) ||
        (o.customer && o.customer.toLowerCase().includes(q)) ||
        (o.phone && o.phone.includes(q))
      );
    }

    // 3. Sorting
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];

      // Handle Date Sorting
      if (sortConfig.key === "date") {
        valA = new Date(valA).getTime();
        valB = new Date(valB).getTime();
      }

      // Handle String Sorting (Names, IDs)
      if (typeof valA === 'string' && typeof valB === 'string') {
        valA = valA.toLowerCase();
        valB = valB.toLowerCase();
      }

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [orders, statusFilter, searchQuery, sortConfig]);


  // --- Helper Functions ---
  const toggleStatusDropdown = (id, e) => {
    e.stopPropagation();
    setOpenPaymentStatusId(null);
    setOpenStatusId(openStatusId === id ? null : id);
  };

  const togglePaymentDropdown = (id, e) => {
    e.stopPropagation();
    setOpenStatusId(null);
    setOpenPaymentStatusId(openPaymentStatusId === id ? null : id);
  };

  const handleChangePaymentStatus = async (orderId, newIsPaid) => {
    const orderToUpdate = orders.find(o => o.id === orderId);
    if (!orderToUpdate || !orderToUpdate.rawOrder) return;

    // Optimistic UI Update
    setOrders(prev => prev.map(o =>
      o.id === orderId
        ? {
          ...o,
          isPaid: newIsPaid,
          rawOrder: {
            ...o.rawOrder,
            isPaid: newIsPaid ? 1 : 0,
            isManual: 0, // نثبتها صفر هنا لأننا في صفحة الطلبات الإلكترونية
            paymentStatus: newIsPaid ? "Paid" : "Unpaid"
          }
        }
        : o
    ));
    setOpenPaymentStatusId(null);

    try {
      const token = localStorage.getItem("authToken");
      const sKey = orderToUpdate.statusKey;
      const payload = {
        ...orderToUpdate.rawOrder,
        orderId: orderToUpdate.orderId,
        orderNo: Number(orderToUpdate.orderNo),
        tenantID: Number(tenantId),
        statusId: getstatusId(sKey),
        status: sKey,
        isPaid: newIsPaid ? 1 : 0,
        paymentStatus: newIsPaid ? "Paid" : "Unpaid",
        details: toPascalDetails(orderToUpdate.rawOrder.details)
      };
      delete payload.id;

      const params = new URLSearchParams();
      params.append("tenantid", tenantId.toString());

      const res = await fetch(`${baseUrl}/UpdateTenantOrder?${params.toString()}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error("Update payment failed");

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم تحديث حالة الدفع" : "Payment status updated",
        timer: 1500,
        showConfirmButton: false,
        position: 'center'
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل تحديث حالة الدفع" : "Failed to update payment status",
        confirmButtonColor: "#5B2C6F"
      });
    }
  };

  const handleChangeStatus = async (orderId, newStatus) => {
    const orderToUpdate = orders.find(o => o.id === orderId);
    if (!orderToUpdate || !orderToUpdate.rawOrder) {
      console.error("Original order data missing");
      return;
    }

    // 1. Optimistic Update (UI)
    const normalized = normalizeStatusKey(newStatus);
    const labelObj = STATUS_TABS.find(t => t.key === normalized);
    const displayLabel = isArabic ? (labelObj?.label_ar || newStatus) : (labelObj?.label_en || newStatus);

    // Optimistic update status
    setOrders(prev => prev.map(o =>
      o.id === orderId
        ? {
          ...o,
          statusKey: normalized,
          originalStatus: displayLabel,
          rawOrder: { ...o.rawOrder, status: newStatus }
        }
        : o
    ));
    setOpenStatusId(null);

    // 2. Prepare Payload
    const payload = {
      ...orderToUpdate.rawOrder,
      orderId: orderToUpdate.orderId,
      orderNo: Number(orderToUpdate.orderNo),
      tenantID: Number(tenantId),
      statusId: getstatusId(normalized),
      status: newStatus,
      isManual: 0, // نثبتها صفر هنا

      isPaid: (orderToUpdate.rawOrder?.isPaid === true || orderToUpdate.rawOrder?.isPaid === 1) ? 1 : 0,
      details: toPascalDetails(orderToUpdate.rawOrder.details)
    };
    delete payload.id;

    // 3. API Call
    try {
      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams();
      params.append("tenantid", tenantId.toString());

      const res = await fetch(`${baseUrl}/UpdateTenantOrder?${params.toString()}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(`Failed to update status`);
      }
      console.log("Status updated successfully");
    } catch (err) {
      console.error("Error updating status:", err);
      alert(isArabic ? "حدث خطأ أثناء تحديث الحالة" : "Error updating status");
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Status Dropdown
      setOpenStatusId(null);
      setOpenPaymentStatusId(null);
      // Sort Dropdown
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
      // Actions Dropdown
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
        setIsActionsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // --- Bulk Actions Handlers ---
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrderIds(filteredOrders.map(o => o.id));
    } else {
      setSelectedOrderIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedOrderIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const [bulkLoading, setBulkLoading] = useState(false);

  const handleBulkStatusSave = async () => {
    if (!bulkStatus) return;
    setBulkLoading(true);

    try {
      const token = localStorage.getItem("authToken");

      // We need to update each order individually as there is no bulk endpoint provided
      const promises = selectedOrderIds.map(async (orderId) => {
        const orderToUpdate = orders.find(o => o.id === orderId);
        if (!orderToUpdate || !orderToUpdate.rawOrder) return;

        const payload = {
          ...orderToUpdate.rawOrder,
          orderId: orderToUpdate.orderId,
          orderNo: Number(orderToUpdate.orderNo),
          tenantID: Number(tenantId),
          statusId: getstatusId(bulkStatus),
          status: bulkStatus,
          isManual: 0, // نثبتها صفر هنا

          isPaid: (orderToUpdate.rawOrder?.isPaid === true || orderToUpdate.rawOrder?.isPaid === 1) ? 1 : 0,
          details: toPascalDetails(orderToUpdate.rawOrder.details)
        };
        delete payload.id;

        const params = new URLSearchParams();
        params.append("tenantid", tenantId.toString());

        return fetch(`${baseUrl}/UpdateTenantOrder?${params.toString()}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify(payload)
        });
      });

      await Promise.all(promises);

      // Refresh orders or update UI locally
      const normalized = normalizeStatusKey(bulkStatus);
      const labelObj = STATUS_TABS.find(t => t.key === normalized);
      const displayLabel = isArabic ? (labelObj?.label_ar || bulkStatus) : (labelObj?.label_en || bulkStatus);

      setOrders(prev => prev.map(o =>
        selectedOrderIds.includes(o.id)
          ? {
            ...o,
            statusKey: normalized,
            originalStatus: displayLabel,
            rawOrder: { ...o.rawOrder, status: bulkStatus }
          }
          : o
      ));

      setShowStatusModal(false);
      setBulkStatus("");
      setSelectedOrderIds([]);

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم تحديث الحالات بنجاح" : "Statuses updated successfully",
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      console.error("Bulk status update error:", err);
      Swal.fire({
        icon: "error",
        title: isArabic ? "حدث خطأ أثناء التحديث الجماعي" : "Error bulk updating",
        confirmButtonColor: "#5B2C6F"
      });
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkPaymentSave = async () => {
    if (bulkPayment === "") return;
    setBulkLoading(true);

    try {
      const token = localStorage.getItem("authToken");
      const isPaidValue = bulkPayment === "paid";

      const promises = selectedOrderIds.map(async (orderId) => {
        const orderToUpdate = orders.find(o => o.id === orderId);
        if (!orderToUpdate || !orderToUpdate.rawOrder) return;

        const payload = {
          ...orderToUpdate.rawOrder,
          orderId: orderToUpdate.orderId,
          orderNo: Number(orderToUpdate.orderNo),
          tenantID: Number(tenantId),
          statusId: getstatusId(orderToUpdate.statusKey),
          isPaid: isPaidValue ? 1 : 0,
          isManual: 0, // نثبتها صفر هنا

          paymentStatus: isPaidValue ? "Paid" : "Unpaid",
          details: toPascalDetails(orderToUpdate.rawOrder.details)
        };
        delete payload.id;

        const params = new URLSearchParams();
        params.append("tenantid", tenantId.toString());

        return fetch(`${baseUrl}/UpdateTenantOrder?${params.toString()}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify(payload)
        });
      });

      await Promise.all(promises);

      setOrders(prev => prev.map(o =>
        selectedOrderIds.includes(o.id)
          ? {
            ...o,
            isPaid: isPaidValue,
            rawOrder: {
              ...o.rawOrder,
              isPaid: isPaidValue ? 1 : 0,
              paymentStatus: isPaidValue ? "Paid" : "Unpaid"
            }
          }
          : o
      ));

      setShowPaymentModal(false);
      setBulkPayment("");
      setSelectedOrderIds([]);

      Swal.fire({
        icon: "success",
        title: isArabic ? "تم تحديث حالة الدفع بنجاح" : "Payment statuses updated",
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      console.error("Bulk payment update error:", err);
      Swal.fire({
        icon: "error",
        title: isArabic ? "حدث خطأ أثناء التحديث الجماعي" : "Error bulk updating",
        confirmButtonColor: "#5B2C6F"
      });
    } finally {
      setBulkLoading(false);
    }
  };

  const allSelected = useMemo(() => {
    return filteredOrders.length > 0 && selectedOrderIds.length === filteredOrders.length && filteredOrders.every(o => selectedOrderIds.includes(o.id));
  }, [filteredOrders, selectedOrderIds]);


  /* ================= Permissions Check (EARLY RETURN) ================= */
  if (!perms.viewOrders) {
    return <NoAccess />;
  }

  if (loading) {
    return (
      <PageContainer title={isArabic ? "الطلبات" : "Orders"}>
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin h-12 w-12 border-b-2 border-purple-600 rounded-full" />
        </div>
      </PageContainer>
    );
  }

  /* ================= Render ================= */
  return (
    <PageContainer>
      {/* --- Header --- */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className={clsx("text-2xl font-bold mb-1", isDark ? "text-white" : "text-gray-900")}>
            {isArabic ? "قائمة الطلبات" : "Orders List"}
          </h1>

        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin-store/orders/create?isManual=0")}
            style={{ backgroundColor: "#5B2C6F" }}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white font-medium transition-all shadow-md "
          >
            <FiPlus size={20} />
            <span>{isArabic ? "إنشاء" : "Create"}</span>
          </button>
        </div>
      </div>


      {/* --- Content Wrapper --- */}
      <div className={clsx(
        "rounded-2xl border",
        isDark ? "bg-[#1e293b] border-[#334155]" : "bg-white border-gray-200"
      )}>

        {/* --- Tabs --- */}
        <div className={clsx(
          "flex items-center gap-1.5 p-3 border-b overflow-x-auto no-scrollbar scroll-smooth",
          isDark ? "border-[#334155] bg-[#0F172A]" : "border-gray-100 bg-gray-50/30"
        )}>
          {STATUS_TABS.map((tab) => {
            const isActive = statusFilter === tab.key;
            const count = statusCounters[tab.key] || 0;
            return (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={clsx(
                  "flex items-center gap-2.5 px-5 py-2.5 rounded-xl whitespace-nowrap text-sm font-bold transition-all duration-300 relative shrink-0",
                  isActive
                    ? "bg-[#5B2C6F] text-white shadow-lg shadow-[#5B2C6F]/20 scale-105"
                    : isDark
                      ? "text-gray-400 "
                      : "text-gray-500 "
                )}
              >
                <span>{isArabic ? tab.label_ar : tab.label_en}</span>
                <span className={clsx(
                  "flex items-center justify-center min-w-[22px] h-5.5 px-1.5 rounded-lg text-[10px] font-black transition-colors",
                  isActive
                    ? "bg-white/20 text-white"
                    : isDark ? "bg-gray-800 text-gray-500" : "bg-gray-200/50 text-gray-400"
                )}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* --- Toolbar --- */}
        <div className="p-4 sm:p-5 flex flex-col lg:flex-row items-center gap-4 bg-white dark:bg-[#1E293B] relative z-50">
          <div className="flex items-center flex-wrap gap-3 w-full lg:w-auto pb-2 lg:pb-0">
            {/* Actions Dropdown */}
            <AnimatePresence>
              {selectedOrderIds.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, x: -10 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.9, x: -10 }}
                  className="relative shrink-0"
                  ref={actionsMenuRef}
                >
                  <button
                    onClick={() => setIsActionsOpen(!isActionsOpen)}
                    className={clsx(
                      "px-4 py-2.5 rounded-xl border-2 transition-all flex items-center justify-between gap-3 min-w-[150px] font-black text-sm",
                      isDark
                        ? "bg-[#2d3a4f] border-[#5B2C6F] text-white shadow-lg shadow-[#5B2C6F]/10 "
                        : "bg-white border-[#5B2C6F] text-[#5B2C6F] shadow-md shadow-[#5B2C6F]/10 "
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <FiLayout size={18} />
                      <span className="uppercase tracking-tighter">{isArabic ? "الإجراءات" : "Actions"}</span>
                      <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-[#5B2C6F] text-white text-xs font-black ring-2 ring-white">
                        {selectedOrderIds.length}
                      </span>
                    </div>
                    <FiChevronDown className={clsx("transition-transform duration-300", isActionsOpen && "rotate-180")} />
                  </button>

                  {isActionsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={clsx(
                        "absolute top-full mt-2 z-[60] rounded-2xl shadow-2xl border w-64 overflow-hidden py-2 rtl:right-0 ltr:left-0",
                        isDark ? "bg-[#1E293B] border-[#334155]" : "bg-white border-gray-100"
                      )}
                    >
                      <button
                        onClick={() => { setShowStatusModal(true); setIsActionsOpen(false); }}
                        className="w-full px-4 py-3 text-sm text-start flex items-center gap-3 "
                      >
                        <div className="p-2 rounded-xl bg-[#5B2C6F]/10 text-[#5B2C6F] ">
                          <FiRotateCcw />
                        </div>
                        <span className="font-bold text-gray-700 dark:text-gray-200">{isArabic ? "تغيير حالة الطلب" : "Change Order Status"}</span>
                      </button>
                      <button
                        onClick={() => { setShowPaymentModal(true); setIsActionsOpen(false); }}
                        className="w-full px-4 py-3 text-sm text-start flex items-center gap-3 "
                      >
                        <div className="p-2 rounded-xl bg-blue-500/10 text-blue-500 ">
                          <FiCreditCard />
                        </div>
                        <span className="font-bold text-gray-700 dark:text-gray-200">{isArabic ? "تغيير حالة الدفع" : "Change Payment Status"}</span>
                      </button>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-center gap-2 shrink-0">
              {/* Sort Menu */}
              <div className="relative" ref={sortMenuRef}>
                <button
                  onClick={() => setIsSortOpen(!isSortOpen)}
                  className={clsx(
                    "px-4 py-2.5 rounded-xl border-2 transition-all flex items-center justify-center gap-2  font-bold text-sm",
                    isDark ? "bg-[#1e293b] border-[#334155] text-gray-300" : "bg-white border-gray-200 text-gray-600"
                  )}
                >
                  <FiFilter className="text-[#5B2C6F]" size={18} />
                  <span>{isArabic ? "فرز حسب" : "Sort By"}</span>
                  <div className="flex flex-col items-center justify-center h-4 w-4">
                    <FiArrowUp size={10} className={clsx("-mb-1", sortConfig.key === 'date' && sortConfig.direction === 'asc' ? "text-[#5B2C6F]" : "opacity-40")} />
                    <FiArrowDown size={10} className={clsx(sortConfig.key === 'date' && sortConfig.direction === 'desc' ? "text-[#5B2C6F]" : "opacity-40")} />
                  </div>
                </button>
                {/* Sort Dropdown */}
                {isSortOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={clsx(
                      "absolute top-full mt-2 z-[60] rounded-2xl shadow-2xl border w-64 flex flex-col overflow-hidden py-1 rtl:right-0 ltr:left-0",
                      isDark ? "bg-[#1E293B] border-[#334155]" : "bg-white border-gray-100"
                    )}
                  >
                    {SORT_OPTIONS.map((opt, idx) => (
                      <button
                        key={idx}
                        onClick={() => { setSortConfig({ key: opt.key, direction: opt.direction }); setIsSortOpen(false); }}
                        className={clsx(
                          "px-4 py-3 text-sm text-start flex items-center justify-between ",
                          sortConfig.key === opt.key && sortConfig.direction === opt.direction && "bg-[#5B2C6F]/10 text-[#5B2C6F] font-black"
                        )}
                      >
                        <span>{isArabic ? opt.label_ar : opt.label_en}</span>
                        {sortConfig.key === opt.key && sortConfig.direction === opt.direction && <FiCheck size={16} />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative flex-1 w-full lg:w-auto">
            <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
              <FiSearch className="text-gray-400 group-focus-within:text-[#5B2C6F] transition-colors" size={20} />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isArabic ? "ابحث عن رقم الطلب او اسم الطلب او رقم الهاتف " : "Search by ID or Name or phone..."}
              className={clsx(
                "w-full py-3 pr-12 pl-4 rounded-2xl border-2 outline-none transition-all font-bold text-sm",
                isDark
                  ? "bg-[#0F172A] border-[#334155] text-white focus:border-[#5B2C6F] focus:ring-4 focus:ring-[#5B2C6F]/10"
                  : "bg-gray-50 border-gray-100 text-gray-900 placeholder-gray-400 focus:bg-white focus:border-[#5B2C6F] focus:ring-4 focus:ring-[#5B2C6F]/10"
              )}
            />
          </div>
        </div>


        {/* --- Table --- */}
        <div className={isDark ? "bg-[#1e293b]" : "bg-white"}>
          <ResponsiveTable
            columns={[
              {
                header: (
                  <input
                    type="checkbox"
                    style={{ accentColor: "#5B2C6F" }}
                    className="w-4 h-4 rounded cursor-pointer"
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />
                ),
                align: "center"
              },
              { header: isArabic ? "رقم الطلب" : "Order No" },
              { header: isArabic ? "العميل" : "Customer", align: "center" },
              { header: isArabic ? "الدفع" : "Payment" },
              { header: isArabic ? "حالة الدفع" : "Payment Status" },
              { header: isArabic ? "المحافظة" : "Governorate" },
              { header: isArabic ? "الشحن" : "Shipping" },
              { header: isArabic ? "المجموع" : "Total" },
              { header: isArabic ? "الحالة" : "Status" },
              { header: isArabic ? "تاريخ الإنشاء" : "Created At" },
              { header: isArabic ? "الإجراءات" : "Actions" },
            ]}
            data={filteredOrders}
            searchable={false}
            sortable={false}
            renderRow={(order) => (
              <>
                {/* Select Checkbox */}
                <td className="py-4 px-4 whitespace-nowrap text-center align-middle">
                  <input
                    type="checkbox"
                    style={{ accentColor: "#5B2C6F" }}
                    className="w-4 h-4 rounded cursor-pointer"
                    checked={selectedOrderIds.includes(order.id)}
                    onChange={() => handleSelectOne(order.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>

                {/* Order ID / Source */}
                <td className="py-4 px-4 whitespace-nowrap align-top">
                  <div className="flex flex-col">
                    <span className="font-bold text-[#5B2C6F] text-base">
                      {order.orderNo}
                    </span>
                    <span className="text-xs text-gray-400 mt-1">
                      {order.isManual
                        ? (isArabic ? "طلب يدوي" : "Manual Order")
                        : (isArabic ? "المتجر الإلكتروني" : "Online Store")
                      }
                    </span>
                  </div>
                </td>

                {/* Customer / Phone */}
                <td className="py-4 px-4 whitespace-nowrap align-top text-center">
                  <div className="flex flex-col items-center text-center">
                    <span className="font-semibold text-sm text-gray-900 dark:text-white">
                      {(() => {
                        const name = order.customer || "-";
                        if (name === "-") return name;
                        const words = name.split(" ");
                        if (words.length <= 2) return name;
                        const lines = [];
                        for (let i = 0; i < words.length; i += 2) {
                          lines.push(words.slice(i, i + 2).join(" "));
                        }
                        return (
                          <div className="flex flex-col items-center">
                            {lines.map((line, idx) => (
                              <span key={idx} className={idx > 0 ? "mt-0.5" : ""}>{line}</span>
                            ))}
                          </div>
                        );
                      })()}
                    </span>
                    <span className="text-xs text-gray-500 mt-1 text-center" dir="ltr">
                      {order.phone || "-"}
                    </span>
                  </div>
                </td>

                {/* Payment Method */}
                <td className="py-4 px-4 whitespace-nowrap align-top">
                  {/* Placeholder for now, check API for exact method field */}
                  <div className="inline-flex items-center gap-1 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs text-gray-600 dark:text-gray-300">
                    {/* Example icon or text */}
                    {order.rawOrder?.paymentMethodName || "Cash"}
                  </div>
                </td>

                {/* Payment Status (Paid/Unpaid) */}
                <td className="py-4 px-4 whitespace-nowrap align-top">
                  <div className="relative">
                    <button
                      onClick={(e) => togglePaymentDropdown(order.id, e)}
                      className={clsx(
                        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border",
                        order.isPaid
                          ? "bg-green-50 text-green-700 border-green-100 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800"
                          : "bg-red-50 text-red-700 border-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800"
                      )}
                    >
                      <span>{order.isPaid ? (isArabic ? "مدفوع" : "Paid") : (isArabic ? "غير مدفوع" : "Unpaid")}</span>
                      <FiChevronDown className={clsx("transition-transform", openPaymentStatusId === order.id && "rotate-180")} size={14} />
                    </button>

                    {/* Payment Dropdown */}
                    {openPaymentStatusId === order.id && (
                      <div
                        className={clsx(
                          "absolute top-full mt-1 z-50 rounded-xl shadow-xl border w-36 flex flex-col overflow-hidden py-1 rtl:right-0 ltr:left-0",
                          isDark ? "bg-[#1e293b] border-[#334155]" : "bg-white border-gray-100"
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => handleChangePaymentStatus(order.id, true)}
                          className={clsx(
                            "px-4 py-2.5 text-sm text-start flex items-center justify-between ",
                            isDark ? "text-gray-200 " : "text-gray-700 ",
                            order.isPaid && "bg-green-50 dark:bg-green-900/20 text-green-600 font-bold"
                          )}
                        >
                          {isArabic ? "مدفوع" : "Paid"}
                          {order.isPaid && <FiCheck size={14} />}
                        </button>
                        <button
                          onClick={() => handleChangePaymentStatus(order.id, false)}
                          className={clsx(
                            "px-4 py-2.5 text-sm text-start flex items-center justify-between ",
                            isDark ? "text-gray-200 " : "text-gray-700 ",
                            !order.isPaid && "bg-red-50 dark:bg-red-900/20 text-red-600 font-bold"
                          )}
                        >
                          {isArabic ? "غير مدفوع" : "Unpaid"}
                          {!order.isPaid && <FiCheck size={14} />}
                        </button>
                      </div>
                    )}
                  </div>
                </td>

                {/* Governorate */}
                <td className="py-4 px-4 whitespace-nowrap align-middle">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {order.governorate || "-"}
                  </span>
                </td>

                {/* Shipping */}
                <td className="py-4 px-4 whitespace-nowrap align-middle">
                  {(() => {
                    const cId = order.rawOrder?.cityId;
                    const del = cId ? deliveryMap[cId] : null;
                    return (
                      <div className="flex flex-col gap-0.5">
                        <span className="inline-flex items-center gap-1 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded text-xs font-bold text-[#5B2C6F] dark:text-purple-300 border border-purple-100 dark:border-purple-800">
                          {del?.name || order.rawOrder?.shippingMethodName || "-"}
                        </span>
                        {del && del.fee > 0 && (
                          <span className="text-[10px] font-bold text-purple-600 pl-1">
                            {del.fee.toLocaleString()} {isArabic ? "د.ع" : "IQD"}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </td>

                {/* Total / Currency */}
                <td className="py-4 px-4 whitespace-nowrap align-top">
                  <div className="flex flex-col">
                    <span className="font-bold text-gray-900 dark:text-white">
                      {(() => {
                        const cId = order.rawOrder?.cityId;
                        const del = cId ? deliveryMap[cId] : null;
                        const total = order.totalAmount + (del ? del.fee : 0);
                        return total.toLocaleString();
                      })()}
                    </span>
                    <span className="text-xs text-gray-400 uppercase">
                      {order.currency || "IQD"}
                    </span>
                  </div>
                </td>

                {/* Status Badge */}
                <td className="py-4 px-4 whitespace-nowrap align-top">
                  <div className="relative">
                    <button
                      onClick={(e) => toggleStatusDropdown(order.id, e)}
                      className={clsx(
                        "text-xs px-3 py-1.5 rounded-lg font-bold flex items-center justify-center min-w-[100px] transition-colors border",
                        order.statusKey === "new" ? "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800" :
                          order.statusKey === "preparing" ? "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-200 dark:border-yellow-800" :
                            order.statusKey === "ready" ? "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-800" :
                              order.statusKey === "delivering" ? "bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-200 dark:border-indigo-800" :
                                order.statusKey === "delivered" ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800" :
                                  order.statusKey === "returned" ? "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-800" :
                                    order.statusKey === "partially_returned" ? "bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900/40 dark:text-teal-200 dark:border-teal-800" :
                                      order.statusKey === "cancelled" ? "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-200 dark:border-red-800" :
                                        order.statusKey === "failed" ? "bg-gray-900 text-white border-gray-800 dark:bg-black dark:text-gray-200 dark:border-gray-700"
                                          : "bg-gray-100 text-gray-800 border-gray-200"
                      )}
                    >
                      {(() => {
                        const sObj = STATUS_TABS.find(t => t.key === order.statusKey);
                        return isArabic ? (sObj?.label_ar || order.statusKey) : (sObj?.label_en || order.statusKey);
                      })()}
                      <FiArrowDown className="ml-2 opacity-70" size={12} />
                    </button>

                    {/* Status Dropdown */}
                    {openStatusId === order.id && (
                      <div
                        className={clsx(
                          "absolute top-full mt-1 z-50 rounded-xl shadow-xl border w-40 flex flex-col overflow-hidden py-1",
                          isDark ? "bg-[#1e293b] border-[#334155]" : "bg-white border-gray-100"
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {ORDER_STATUSES.map((status) => {
                          const label = isArabic ? status.ar : status.en;
                          return (
                            <button
                              key={status.key}
                              onClick={() => handleChangeStatus(order.id, status.key)}
                              className={clsx(
                                "px-4 py-2 text-sm text-start hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors",
                                isDark ? "text-gray-200 " : "text-gray-700 "
                              )}
                            >
                              {label}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </td>

                {/* Dates: Created / Updated */}
                <td className="py-4 px-4 whitespace-nowrap align-top">
                  <div className="flex flex-col text-right">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {order.date}
                    </span>
                    <span className="text-xs text-gray-400 mt-1">
                      {/* Assuming rawDate or fallback to same date for now if updated date missing */}
                      {order.date}
                    </span>
                  </div>
                </td>

                {/* Actions */}
                <td className="py-4 px-4 whitespace-nowrap text-center align-top">
                  <button
                    onClick={() => navigate(`/admin-store/orders-details/${order.id}`)}
                    className={clsx(
                      "p-2 rounded-lg transition-colors",
                      isDark
                        ? "bg-gray-800 text-gray-400 "
                        : "bg-gray-50 text-gray-500 "
                    )}
                  >
                    <FiMoreHorizontal size={20} />
                  </button>
                </td>
              </>
            )}

          />
        </div>
      </div>

      {/* --- Modals --- */}
      <AnimatePresence>
        {/* Status Bulk Update Modal */}
        {showStatusModal && (
          <BulkActionModal
            title={isArabic ? "تغيير حالة الطلب" : "Change Order Status"}
            isArabic={isArabic}
            isDark={isDark}
            onClose={() => setShowStatusModal(false)}
            onSave={handleBulkStatusSave}
            loading={bulkLoading}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              {ORDER_STATUSES.map((status) => {
                const isActive = bulkStatus === status.key;
                return (
                  <button
                    key={status.id}
                    onClick={() => setBulkStatus(status.key)}
                    className={clsx(
                      "flex items-center justify-between p-4 rounded-xl border transition-all",
                      isActive
                        ? "border-[#5B2C6F] bg-purple-50 dark:bg-purple-900/20 text-[#5B2C6F]"
                        : "border-gray-200 dark:border-gray-700"
                    )}
                  >
                    <span className="font-bold text-sm">{isArabic ? status.ar : status.en}</span>
                    {/* الدائرة الخاصة بالاختيار */}
                    <div className={clsx(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center",
                      isActive ? "border-[#5B2C6F]" : "border-gray-300"
                    )}>
                      {isActive && <div className="w-2.5 h-2.5 rounded-full bg-[#5B2C6F]" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </BulkActionModal>
        )}

        {/* Payment Bulk Update Modal */}
        {showPaymentModal && (
          <BulkActionModal
            title={isArabic ? "تغيير حالة الدفع" : "Change Payment Status"}
            isArabic={isArabic}
            isDark={isDark}
            onClose={() => setShowPaymentModal(false)}
            onSave={handleBulkPaymentSave}
            loading={bulkLoading}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              {[
                { key: "paid", label_ar: "مدفوع", label_en: "Paid" },
                { key: "unpaid", label_ar: "غير مدفوع", label_en: "Unpaid" }
              ].map((opt) => {
                const label = isArabic ? opt.label_ar : opt.label_en;
                const isActive = bulkPayment === opt.key;

                return (
                  <button
                    key={opt.key}
                    onClick={() => setBulkPayment(opt.key)}
                    className={clsx(
                      "flex items-center justify-between p-4 rounded-xl border transition-all",
                      isActive
                        ? "border-[#5B2C6F] bg-purple-50 dark:bg-purple-900/20 text-[#5B2C6F] dark:text-purple-300 ring-2 ring-[#5B2C6F]/10"
                        : "border-gray-200 dark:border-gray-700 "
                    )}
                  >
                    <span className="font-bold text-sm tracking-wide">{label}</span>
                    <div className={clsx(
                      "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors",
                      isActive ? "border-[#5B2C6F] bg-[#5B2C6F]/10" : "border-gray-300 dark:border-gray-600"
                    )}>
                      {isActive && <div className="w-2.5 h-2.5 rounded-full bg-[#5B2C6F]" />}
                    </div>
                  </button>
                )
              })}
            </div>
          </BulkActionModal>
        )}
      </AnimatePresence>
    </PageContainer>
  );
}

/* ================= Bulk Action Modal Component ================= */
function BulkActionModal({ title, isArabic, isDark, onClose, onSave, loading, children }) {
  return (
    // تم رفع الـ z-index إلى 9999 لضمان ظهوره فوق النافبار
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-hidden">

      {/* غطاء خلفي (Backdrop) مع تعتيم أقوى لضمان عدم ظهور بياض النافبار */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        // تم تغيير bg-black/40 إلى bg-black/70 وتغيير absolute إلى fixed
        className="fixed inset-0 bg-black/70 backdrop-blur-md"
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className={clsx(
          "relative w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl flex flex-col z-[10000]",
          isDark ? "bg-[#1e293b]" : "bg-white"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <h2 className={clsx("text-xl font-black", isDark ? "text-white" : "text-gray-900")}>
            {title}
          </h2>
          <button
            onClick={onClose}
            className={clsx(
              "p-2 rounded-lg transition-colors",
              isDark ? "bg-red-500/10 text-red-500 " : "bg-red-50 text-red-500 "
            )}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-8 max-h-[70vh] overflow-y-auto no-scrollbar">
          {children}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 dark:border-gray-800 flex items-center gap-4">
          <button
            onClick={onSave}
            disabled={loading}
            className="flex-1 py-3.5 rounded-2xl bg-[#5B2C6F] text-white font-bold text-base transition-all shadow-lg shadow-purple-900/20 disabled:opacity-50"
          >
            {loading ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
          </button>
          <button
            onClick={onClose}
            className={clsx(
              "flex-1 py-3.5 rounded-2xl font-bold text-base border transition-all",
              isDark ? "border-gray-700 text-gray-400 " : "border-gray-200 text-gray-500 "
            )}
          >
            {isArabic ? "إلغاء" : "Cancel"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
